import React, { useState, useEffect } from 'react';

const DynamicForm = ({ structure, campName, onSubmit, isSubmitting }) => {
  const [formData, setFormData] = useState({});
  const [validationErrors, setValidationErrors] = useState({});

  useEffect(() => {
    setFormData({});
    setValidationErrors({});
  }, [structure]);

  const handleChange = (id, value) => {
    setFormData((prev) => ({ ...prev, [id]: value }));
    if (validationErrors[id]) {
      setValidationErrors((prev) => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
    }
  };

  const validateForm = () => {
    if (!structure || !structure.fields) return false;
    const errors = {};

    structure.fields.forEach((field) => {
      if (!field.required) return;
      const value = formData[field.id];

      if (field.type === 'checkbox') {
        if (!value || !Array.isArray(value) || value.length === 0) {
          errors[field.id] = 'Välj minst ett alternativ.';
        }
      } else if (field.type === 'radio' || field.type === 'select') {
        if (!value) errors[field.id] = 'Välj ett alternativ.';
      } else {
        if (!value || String(value).trim() === '') errors[field.id] = 'Detta fält är obligatoriskt.';
      }
    });

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!validateForm()) {
      let firstError = null;
      for (const field of structure.fields) {
        if (field.required) {
          const value = formData[field.id];
          let hasErr = false;
          if (field.type === 'checkbox') hasErr = !value || !Array.isArray(value) || value.length === 0;
          else if (field.type === 'radio' || field.type === 'select') hasErr = !value;
          else hasErr = !value || String(value).trim() === '';
          if (hasErr) { firstError = field.id; break; }
        }
      }
      if (firstError) {
        const el = document.getElementById(`field-${firstError}`);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    const processedData = {};
    structure.fields.forEach((field) => {
      const value = formData[field.id];
      if (field.type === 'checkbox' && Array.isArray(value)) {
        processedData[field.id] = value.join(', ');
      } else {
        processedData[field.id] = value || '';
      }
    });

    onSubmit(processedData);
  };

  if (!structure || !structure.fields) {
    return <p className="text-red-500">Ett fel uppstod: Formuläret saknar fält.</p>;
  }

  return (
    <form onSubmit={handleSubmit} className="animate-in fade-in duration-300" noValidate>
      <style>{`
        .stoggle {
          position: relative;
          width: 44px;
          height: 24px;
          border-radius: 12px;
          background: #e5e7eb;
          transition: all 0.3s ease;
          flex-shrink: 0;
          cursor: pointer;
        }
        .stoggle.on { background: #003660; }
        .stoggle::after {
          content: '';
          position: absolute;
          top: 3px;
          left: 3px;
          width: 18px;
          height: 18px;
          border-radius: 50%;
          background: #fff;
          box-shadow: 0 1px 3px rgba(0,0,0,0.1);
          transition: transform 0.3s ease;
        }
        .stoggle.on::after { transform: translateX(20px); }
        .stoggle-row {
          display: flex;
          align-items: center;
          gap: 16px;
          padding: 14px 20px;
          cursor: pointer;
          user-select: none;
          transition: background-color 0.2s;
          border-radius: 12px;
        }
        .stoggle-row:hover { background-color: #f9fafb; }
        .stoggle-label { font-size: 0.95rem; color: #4b5563; font-weight: 600; }
        .stoggle-row.selected .stoggle-label { color: #003660; font-weight: 700; }
      `}</style>

      {/* ── FORM FIELDS CARDS ── */}
      <div className="flex flex-col">
        {structure.fields.map((field) => {
          const fieldLabel = field.title || field.label || '';
          const hasError = !!validationErrors[field.id];

          return (
            <div
              key={field.id}
              id={`field-${field.id}`}
              className="bg-white rounded-xl border border-gray-200 p-6 sm:p-8 mb-3"
            >
              <label className="block text-base font-semibold text-[#003360] mb-2">
                {fieldLabel}
                {field.required && <span className="text-red-500 ml-1"> *</span>}
              </label>

              {field.helpText && (
                <p className="text-sm text-gray-500 mb-3">{field.helpText}</p>
              )}

              <div className="mt-2">
                {/* TEXT */}
                {field.type === 'text' && (
                  <input
                    type="text"
                    className={`w-full py-3 px-4 text-base border rounded-lg outline-none transition-all ${hasError ? 'border-red-300 bg-red-50/10 focus:border-red-500' : 'border-gray-300 focus:border-[#003360] focus:ring-2 focus:ring-[#003360]/20'
                      }`}
                    onChange={(e) => handleChange(field.id, e.target.value)}
                    value={formData[field.id] || ''}
                  />
                )}

                {/* TEXTAREA */}
                {field.type === 'textarea' && (
                  <textarea
                    rows={3}
                    className={`w-full py-3 px-4 text-base border rounded-lg outline-none transition-all resize-y ${hasError ? 'border-red-300 bg-red-50/10 focus:border-red-500' : 'border-gray-300 focus:border-[#003360] focus:ring-2 focus:ring-[#003360]/20'
                      }`}
                    onChange={(e) => handleChange(field.id, e.target.value)}
                    value={formData[field.id] || ''}
                  />
                )}

                {/* RADIO / TOGGLES */}
                {field.type === 'radio' && field.options && field.options.length > 0 && (
                  <div className="space-y-2">
                    {field.options.map((option) => {
                      const isOn = formData[field.id] === option;
                      return (
                        <div
                          key={option}
                          className={`stoggle-row ${isOn ? 'selected' : ''}`}
                          onClick={() => handleChange(field.id, option)}
                        >
                          <div className={`stoggle ${isOn ? 'on' : ''}`} />
                          <span className="stoggle-label">{option}</span>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* CHECKBOX / TOGGLES */}
                {field.type === 'checkbox' && field.options && field.options.length > 0 && (
                  <div className="space-y-2">
                    {field.options.map((option) => {
                      const vals = formData[field.id] || [];
                      const isOn = vals.includes(option);
                      return (
                        <div
                          key={option}
                          className={`stoggle-row ${isOn ? 'selected' : ''}`}
                          onClick={() => {
                            if (isOn) handleChange(field.id, vals.filter(v => v !== option));
                            else handleChange(field.id, [...vals, option]);
                          }}
                        >
                          <div className={`stoggle ${isOn ? 'on' : ''}`} />
                          <span className="stoggle-label">{option}</span>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* SELECT */}
                {field.type === 'select' && field.options && field.options.length > 0 && (
                  <div className="relative">
                    <select
                      className={`w-full py-3 px-4 pr-10 text-base border rounded-lg outline-none transition-all appearance-none cursor-pointer ${hasError ? 'border-red-300 bg-red-50/10 focus:border-red-500' : 'border-gray-300 focus:border-[#003360] focus:ring-2 focus:ring-[#003360]/20'
                        }`}
                      onChange={(e) => handleChange(field.id, e.target.value)}
                      value={formData[field.id] || ''}
                    >
                      <option value="">-- Välj i listan --</option>
                      {field.options.map((opt) => <option key={opt} value={opt}>{opt}</option>)}
                    </select>
                    <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-gray-400">
                      <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5"><path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" /></svg>
                    </div>
                  </div>
                )}

                {hasError && (
                  <div className="text-red-500 text-sm mt-3 font-bold flex items-center">
                    
                    {validationErrors[field.id]}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Submit button */}
      <div className="mt-3">
        <button
          type="submit"
          disabled={isSubmitting}
          className="w-full py-4 rounded-lg text-base font-semibold text-white bg-[#003360] hover:bg-[#00294d] transition-all duration-200 disabled:opacity-50 shadow-sm"
        >
          {isSubmitting ? 'Behandlar anmälan...' : 'Gå vidare till betalning'}
        </button>
      </div>
    </form>
  );
};

export default DynamicForm;